import { isObject } from '@/global/utils/utils'
import { dateService, FORMATS } from '@/global/services/date'
import indexTableLeafletMapHelper from '../../../common/config/index-table-leaflet-map-helper'
import truncatedTextDisplay from '@/global/components/TruncatedTextDisplay.vue'
import { showDetailsAction } from '@/global/components/table/helpers/tableActions'
import { STATUS_PILL_CELL_CONFIG } from '../../../common/config/status-cell-helper'

export default {
  name: 'mainTableConfig',

  data () {
    return {
      invalidateTableSize: false
    }
  },

  mixins: [
    indexTableLeafletMapHelper
  ],

  computed: {
    tableConfig () {
      return {
        tableConfig: {
          uiView: 'eRegulation_roads_info_index',
          apiConfig: {
            get: {
              module: 'road-maintenance',
              route: 'traffic-activity-feed',
              method: 'get'
            }
          },
          autoHeight: false,
          fillHeight: true,
          invalidateSize: this.invalidateTableSize,
          rowDblClick: (event) => this.showDetails(event),
          onRefreshData: () => this.fetchPolylines(),
          filters_and_columns_visibility_config: {
            // Nothing is filterable on this listing, so it is not offered - see the
            // empty `filtersConfig` below.
            showFiltersButton: false,
            showNewBtn: !!this.permissions?.canCreate,
            newClick: () => this.onNewRoadInformation(),
            newBtnPosition: 'start'
          },
          globalStyle: {
            tableRowClasses: 'table-row',
            sideTableContentColClasses: 'table-side-col'
          },
          tableConfig: {
            cellsConfig: {
              // An event is one row but several streets, and the closure type now
              // belongs to the street - so both columns report every distinct type
              // its streets carry, which is what the backend hands over.
              type: {
                custom_display_value: (event) => this.joinTypeDescriptions(event?.type)
              },
              subtype: {
                custom_display_value: (event) => this.joinTypeDescriptions(event?.subtype)
              },
              publish_to_public: {
                custom_display_value: (event) => {
                  return this.$t(event?.publish_to_public ? 'base.yes' : 'base.no')
                }
              },
              start_date_time: {
                custom_display_value: (event) => {
                  return event?.start_date_time ? dateService.formatCustom(event.start_date_time, FORMATS.isoDatetime, FORMATS.datetimeShort) : ''
                }
              },
              end_date_time: {
                custom_display_value: (event) => {
                  return event?.end_date_time ? dateService.formatCustom(event.end_date_time, FORMATS.isoDatetime, FORMATS.datetimeShort) : ''
                }
              },
              created_at: {
                custom_display_value: (event) => {
                  return event?.created_at ? dateService.formatCustom(event.created_at, FORMATS.isoDatetime, FORMATS.datetimeShort) : ''
                }
              },
              traffic_regulation: {
                custom_display_value: (event) => {
                  return event?.traffic_regulation?.name || ''
                }
              },
              status: {
                type: 'status',
                custom_display_value: (data) => {
                  if (data && data.status && isObject(data.status)) {
                    return {
                      ...data.status,
                      ...{
                        description: data.status.description ? this.$t(data.status.description) : '-'
                      }
                    }
                  }
                },
                displayValue: 'description',
                colorObjectKey: 'color',
                textColorKey: 'text',
                backgroundColorKey: 'background',
                ...STATUS_PILL_CELL_CONFIG
              }
            },
            headers: [
              {
                label: this.$t('fleet/documents/prl_cargo.sr_no'),
                key: 'id',
                always_visible: true
              },
              {
                label: this.$t('road-maintenance/aggregator.street'),
                key: 'street',
                component: truncatedTextDisplay,
                always_visible: true
              },
              {
                label: this.$t('fleet/vehicle.status'),
                key: 'status',
                style: {
                  width: '120px'
                },
                always_visible: true
              },
              {
                label: this.$t('road-maintenance/aggregator.closure_type'),
                key: 'type',
                initial_value: true
              },
              {
                label: this.$t('road-maintenance/aggregator.closure_subtype'),
                key: 'subtype',
                initial_value: true
              },
              {
                label: this.$t('road-maintenance/aggregator.road_closure_start_time'),
                key: 'start_date_time',
                initial_value: true
              },
              {
                label: this.$t('road-maintenance/aggregator.road_closure_end_time'),
                key: 'end_date_time',
                initial_value: true
              },
              {
                label: this.$t('road-maintenance/aggregator.publish_to_public'),
                key: 'publish_to_public',
                style: {
                  width: '150px'
                },
                initial_value: true
              },
              {
                label: this.$t('road-maintenance/aggregator.request'),
                key: 'traffic_regulation',
                initial_value: true
              },
              {
                label: this.$t('road-maintenance/aggregator.creation_date'),
                key: 'created_at',
                initial_value: true
              }
            ],
            paginationConfig: {
              perPageOptions: [20, 50, 100],
              perPage: 20,
              currentPage: 1
            },
            actionsConfig: [
              showDetailsAction(this.$t, (event) => this.showDetails(event))
            ],
            filtersConfig: {
            }
          }
        },
        split: {
          onResize: () => this.invalidateMapSize(),
          children: [
            {
              component: this.mainTableMapComponent,
              showStatusLegend: false,
              mapInitialized: async (event) => await this.mapInitialized(event, false),
              mapStyle: {
                borderBottomLeftRadius: 'unset !important',
                borderBottomRightRadius: 'unset !important'
              },
              paneStyle: {
                borderBottomLeftRadius: 'unset !important',
                borderBottomRightRadius: 'unset !important'
              }
            }
          ]
        },
        breadcrumbs: {
          items: [
            {
              text: this.$t('road-maintenance.eRegulation')
            },
            {
              text: this.$t('road-maintenance.road_information'),
              bolded: true
            }
          ]
        }
      }
    }
  },

  methods: {
    /**
     * The backend reports a row's closure types as the distinct set across the
     * event's streets, so a cell is a list - one type most of the time, more when
     * the streets differ.
     */
    joinTypeDescriptions (types) {
      if (!Array.isArray(types)) {
        return types?.description ? this.$t(types.description) : ''
      }

      return types
        .map(type => (type?.description ? this.$t(type.description) : ''))
        .filter(Boolean)
        .join(', ')
    },

    /**
     * Opens a road information record. The id is the feed record's own - what the
     * listing shows as the serial number, and what `traffic-activity-feed/{id}` reads.
     */
    openDetails (id, fromRequest = false) {
      this.$router.push({
        name: 'Aggregator_road_information_details',
        params: {
          id
        },
        query: {
          category: 'request_overview',
          // An event raised from a traffic regulation request takes its schedule
          // from that request, and the details screen locks it. The row knows;
          // the record the screen fetches does not report it (yet), so the answer
          // travels in the URL - where a reload keeps it.
          ...(fromRequest ? { from_request: 1 } : {})
        }
      }).then()
    },

    handlePolylineClick (polyline) {
      this.openDetails(polyline?.id)
    },

    showDetails (event) {
      const { data } = event || {}
      this.openDetails(data?.id, !!data?.traffic_regulation?.id)
    },

    onNewRoadInformation () {
      this.$router.push({
        name: 'Aggregator_road_information_details',
        query: {
          category: 'request_new'
        }
      }).then()
    }
  }
}
